import React from 'react';
import { CmsContent, ConfigKey, Room, EVENT_FLOW_CONFIGS, STEP_COMPONENT } from '../../../../common/proctorStepsTypes';
import { createCleanMarkup } from '../../../../../../utils/common';
import StepContentPanel from '../../../../common/StepContentPanel';
import StepContentTab from '../../../../common/StepContentTab';
import RoomCode from '../../../../common/RoomCode';

interface GetStepDefinitionsProps {
  cmsContent: CmsContent | undefined;
  stepNum: number;
  isInSchoolAPEvents: boolean;
  isInSchoolPSATEvent: boolean;
  isInSchoolSATEvent: boolean;
  isInSchoolAPEvent: boolean;
  isInSchoolAPHybridEvent: boolean;
  configKey: ConfigKey;
  room: Room;
}

interface StepDefinition {
  introStep: number;
  outerStep: number;
  steps: () => React.ReactElement | undefined;
}

export function GetStepDefinitions({
  cmsContent,
  stepNum,
  isInSchoolAPEvents,
  isInSchoolPSATEvent,
  isInSchoolAPEvent,
  isInSchoolAPHybridEvent,
  configKey,
  isInSchoolSATEvent,
  room,
}: GetStepDefinitionsProps): StepDefinition {
  const { introStep, outerStep, progressStepperMax, steps: stepConfig } = EVENT_FLOW_CONFIGS[configKey].setup;

  const stepContent = cmsContent?.steps?.find((n) => n.step_number === stepNum);
  const mainPanelData = <div dangerouslySetInnerHTML={createCleanMarkup(stepContent?.main_panel_content)} />;
  const detailPanelData = <div dangerouslySetInnerHTML={createCleanMarkup(stepContent?.detail_panel_content)} />;

  const content = {
    main_panel_content: mainPanelData,
    main_panel_header: stepContent?.main_panel_header,
    detail_panel_content: detailPanelData,
    detail_panel_header: stepContent?.detail_panel_header,
  };

  const stepComponents: Partial<Record<STEP_COMPONENT, React.ReactElement>> = {
    aboutAPExam: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='About Digital AP Exams'
        content={content}
        desc={<div>{mainPanelData}</div>}
        //TODO: AOPS-57732: check image and description for step #1
        img={
          <div
            className={`${
              isInSchoolAPHybridEvent
                ? 'hybrid-digital-svg'
                : isInSchoolAPEvent
                  ? 'fully-digital-svg'
                  : 'setup-step-1-svg'
            } step-illustration-img mt-4`}
          />
        }
        imgDesc={`Your room should have ${room?.capacity} seats.`}
      />
    ),
    countSeats: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Count Seats'
        content={content}
        desc={<div>{mainPanelData}</div>}
        //TODO: AOPS-57732: check image and description for step #2
        img={<div className='wknd-sat-setup-step-2-svg step-illustration-img mt-4' />}
        imgDesc={`Your room should have ${room?.capacity} seats.`}
      />
    ),
    checkSpacing: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Check Spacing'
        content={content}
        desc={<div>{mainPanelData}</div>}
        img={
          <div
            className={`${
              isInSchoolPSATEvent ? 'psat-setup-step-2-svg' : 'setup-step-2-svg'
            } step-illustration-img mt-4`}
          />
        }
        imgDesc='Students must be separated by at least 3 feet on the right and left.'
      />
    ),
    checkSupplies: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Check Supplies'
        content={content}
        desc={<div>{mainPanelData}</div>}
        img={
          <div
            className={`${
              isInSchoolPSATEvent
                ? 'psat-setup-step-3-svg'
                : isInSchoolAPEvents
                  ? 'ap-setup-step-3-svg'
                  : 'setup-step-3-svg'
            } step-illustration-img mt-4`}
          />
        }
        imgDesc='No maps, charts or other teaching materials should be visible.'
      />
    ),
    checkMaterials: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Check Test Materials and Support Staff'
        content={content}
        desc={<div>{mainPanelData}</div>}
        img={<div className='setup-step-4-svg step-illustration-img mt-4' />}
        imgDesc='Place 3 sheets of scratch paper on each desk.'
      />
    ),
    distributeSupplies: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Distributing and Collecting Supplies'
        content={content}
        desc={<div>{mainPanelData}</div>}
        img={
          <div
            className={`${
              isInSchoolAPHybridEvent ? 'ap-setup-step-3-svg' : isInSchoolAPEvent ? 'post-it-svg' : 'setup-step-4-svg'
            } step-illustration-img mt-4`}
          />
        }
        imgDesc='Place 3 sheets of scratch paper on each desk.'
      />
    ),
    checkWifi: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Test the Wi-Fi'
        content={content}
        desc={<div>{mainPanelData}</div>}
        img={
          <div
            className={`${
              isInSchoolAPHybridEvent ? 'post-it-svg' : isInSchoolAPEvent ? 'post-it-svg' : 'setup-step-4-svg'
            } step-illustration-img mt-4`}
          />
        }
        imgDesc='Place 3 sheets of scratch paper on each desk.'
      />
    ),
    distributeSignInTickets: (
      <StepContentPanel
        room={room}
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Distribute Sign-In Tickets'
        content={content}
        desc={<div>{mainPanelData}</div>}
        img={
          isInSchoolSATEvent ? (
            <div className='setup-step-5-inschool-sat-svg step-illustration-img mt-4' />
          ) : (
            <div
              className={`${
                isInSchoolPSATEvent ? 'psat-setup-step-5-svg' : 'setup-step-5-svg'
              } step-illustration-img mt-4`}
            />
          )
        }
        imgDesc='You should have a bin, labels, and bags or envelopes for prohibited devices.'
      />
    ),
    writeInstructions: (
      <StepContentTab
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Write Instructions on the Board'
        invertStyle={true}
        subHeading={<div className='wizard-subHeading'>{content.detail_panel_content}</div>}
        panelHeading='Write these instructions on the board (add the Wi-Fi password if you need to):'
        content={content}
        desc={
          <div className='row row-eq-height'>
            <div className='col-xs-12 col-md-6 py-3'>
              <div>{mainPanelData}</div>
            </div>
            <div className='col-xs-12 col-md-6 wizard-room-code-wrapper py-3'>
              <div style={{ fontSize: '1.5rem', lineHeight: '1.75rem' }}>Room Code:</div>
              <div style={{ fontSize: '3.75rem', lineHeight: '4.375rem' }} className='mt-3'>
                <RoomCode code={room?.joinCode} />
              </div>
            </div>
          </div>
        }
      />
    ),
    collectProhibitedItems: (
      <StepContentPanel
        showProgressBar={true}
        stepNum={stepNum}
        progressStepperMax={progressStepperMax}
        heading='Prepare to Collect Prohibited Items'
        panelHeading='Write these instructions on the board (add the Wi-Fi password if you need to):'
        content={content}
        desc={<div>{mainPanelData}</div>}
        //TODO: AOPS-57732: check image and description for step #6
        img={<div className='setup-step-5-svg step-illustration-img mt-4' />}
        imgDesc={`Your room should have ${room?.capacity} seats.`}
      />
    ),
  };

  return {
    introStep,
    outerStep,
    steps: () => {
      const rendererName = stepConfig[stepNum];
      return rendererName ? stepComponents[rendererName] : undefined;
    },
  };
}
